import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  RadioGroup,
  Radio,
  Checkbox,
  InputLabel,
  Grid,
  ThemeProvider,
  createTheme,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";

import CustomPayPalSubscriptionButton from "../payPalButtons/PayPalSubscriptionButton";

import "./buyNow.css";

import appSettings from "../../data/appSettings";
const plans = appSettings.PRODUCT_PLANS;
const promotions = appSettings.PROMOTIONS;

////Check if the email address is valid or not
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

///Dark theme
const darkTheme = createTheme({
  palette: {
    palette: {
      mode: "dark",
      primary: { main: "#90caf9" },
      background: {
        default: "#FFFFFF",
        paper: "#ffffff",
      },
    },
    shape: { borderRadius: 8 },
  },
});

// Currency formatter
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export default function BuyNow() {
  // const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [pricePerItem, setPricePerItem] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [licenseType, setLicenseType] = useState("standalone");

  const [showAlternativeAddress, setShowAlternativeAddress] = useState(false);

  const [emailAddress, setEmailAddress] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [showPayPal, setShowPayPal] = useState(false);
  const [loadingPayPal, setLoadingPayPal] = useState(false);

  const [overlayMessage, setOverlayMessage] = useState("");
  const [overlaySubMessage, setOverlaySubMessage] = useState("");
  const [showOverlay, setShowOverlay] = useState(false);

  // useEffect(() => {
  //   fetch('../../data/plans.json')
  //     .then(
  //       (res) => res.json()
  //     )
  //     .then((data) => setPlans(data))
  //     .catch((err) => console.error('Failed to load plans:', err));
  // }, []);

  const isPayButtonDisabled =
    !selectedPlanId ||
    quantity <= 0 ||
    (showAlternativeAddress &&
      (!isValidEmail(emailAddress) || !firstName || !lastName));

  const resetPayPalButtonState = () => {
    setShowPayPal(false);
    setLoadingPayPal(false);
  };

  useEffect(() => {
    const plan = plans.find((p) => p.id === selectedPlanId);
    if (plan && quantity > 0) {
      //if plan has promotions, find the correct price range based on quantity
      if (plan.promotion_id) {
        const promotion = promotions.find((p) => p.id === plan.promotion_id);
        if (promotion) {
          const ranges = promotion.discountRanges
            // .map((r) => ({
            //   starting_quantity: Number(r.starting_quantity),
            //   discountPercentage: Number(r.discountPercentage),
            // }))
            .sort((a, b) => a.starting_quantity - b.starting_quantity);

          let discount = 0;

          for (const range of ranges) {
            if (quantity >= range.starting_quantity) {
              discount = range.discountPercentage;
            } else {
              break;
            }
          }
          const discountedPrice = plan.base_price * (1 - discount / 100);
          setDiscount(discount);
          setPricePerItem(discountedPrice);
          setTotalPrice(discountedPrice * quantity);
          return;
        }
      }

      const range = plan.priceRanges.find(
        (r) =>
          quantity >= r.starting_quantity &&
          (r.ending_quantity == null || quantity <= r.ending_quantity),
      );
      if (range) {
        setDiscount(0);
        setPricePerItem(range.amount.value);
        setTotalPrice(range.amount.value * quantity);
      } else {
        setDiscount(0);
        setPricePerItem(0);
        setTotalPrice(0);
      }
    }
  }, [selectedPlanId, quantity, plans]);

  return (
    <div className="da__buyNow-container section__padding">
      <div className="da__product-container">
        <h1
          className="gradient__text"
          style={{ fontSize: "36px", lineHeight: "45px", fontWeight: "800" }}
        >
          Purchase Our Product Now
        </h1>
        <h2
          className="plan-title"
          style={{ color: "white", fontWeight: "normal" }}
        >
          Select a Product Plan and number of licenses, then proceed with the
          secured payment system powered by PayPal to start your annual
          subscription. You can cancel your subscription at any time.
          <br />
          Depending on selected product, you will get additional 5% discount of
          the total price for every 5 licenses purchased (up to 40% discount).
          So the more you buy, the more you save!
        </h2>
        <ThemeProvider theme={darkTheme}>
          <Box
            className="plan-container"
            sx={{
              width: "100%",
              maxWidth: "1000px", // set the max width here
              margin: "0 auto", // center it
              display: "flex-start",
              flexDirection: "column",
              justifyContent: "left",
              alignItems: "left",
              px: 1,
              py: 1,
            }}
          >
            <Paper
              className="plan-paper"
              sx={{
                width: "100%",
                margin: "0 auto", // center it
                p: 3,
                backgroundColor: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(4px)",
                borderRadius: 2,
              }}
            >
              <Stack spacing={2} sx={{ maxWidth: "100%" }}>
                <FormControl fullWidth sx={{ mb: 2 }} error={!selectedPlanId}>
                  <InputLabel id="plan-select-label">Select Product</InputLabel>
                  <Select
                    labelId="plan-select-label"
                    value={selectedPlanId}
                    label="Select Product"
                    onChange={(e) => {
                      setSelectedPlanId(e.target.value);
                      resetPayPalButtonState();
                    }}
                    renderValue={(selected) => {
                      const selectedPlan = plans.find((p) => p.id === selected);
                      return selectedPlan ? selectedPlan.name : "";
                    }}
                  >
                    {plans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        <Box sx={{ width: "100%", whiteSpace: "normal" }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {plan.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: "normal" }}
                          >
                            {plan.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedPlanId && (
                    <FormHelperText>
                      Please select a product plan
                    </FormHelperText>
                  )}
                </FormControl>

                <TextField
                  label="Number of Licenses"
                  type="number"
                  fullWidth
                  value={quantity}
                  onChange={(e) => {
                    var number = parseInt(e.target.value, 10);
                    setQuantity(number);
                    if (number < 3) {
                      setLicenseType("standalone");
                    }
                    resetPayPalButtonState();
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ mb: 2 }}
                />
                {/* Select License type */}
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend" style={{ fontWeight: "bold" }}>
                    License Type
                  </FormLabel>
                  <RadioGroup
                    sx={{ ml: 2 }}
                    row
                    value={licenseType}
                    onChange={(e) => setLicenseType(e.target.value)}
                  >
                    <FormControlLabel
                      value="standalone"
                      control={<Radio color="primary" />}
                      label="Standalone License"
                    />
                    <FormControlLabel
                      value="network"
                      control={<Radio color="primary" />}
                      label="Network License"
                      disabled={quantity < 3}
                    />
                  </RadioGroup>

                  {quantity < 3 && (
                    <FormLabel
                      sx={{ ml: 6, mt: 0, mb: 2 }}
                      style={{ color: "darkred" }}
                    >
                      You need to purchase at least 3 licenses to enable Network
                      License option.
                    </FormLabel>
                  )}
                </FormControl>

                {/* OTHER EMAIL ADDRESS */}
                <FormControlLabel
                  style={{
                    marginTop: "1px",
                    marginBottom: "-10px",
                    marginLeft: "-10px",
                  }}
                  control={
                    <Checkbox
                      checked={showAlternativeAddress}
                      onChange={(e) =>
                        setShowAlternativeAddress(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Receive license with Email Address different from PayPal's email address"
                />

                {showAlternativeAddress && (
                  <>
                    <TextField
                      label="Email Address"
                      type="email"
                      width="100%"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      error={!isValidEmail(emailAddress)}
                      helperText={
                        !isValidEmail(emailAddress)
                          ? "Please enter a valid email address to send License information"
                          : ""
                      }
                      sx={{ mb: 2 }}
                    />
                    {/* FIRST NAME LAST NAME */}
                    <div className="name-fields">
                      <TextField
                        label="First Name"
                        fullWidth
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        error={!firstName}
                        helperText={
                          !firstName ? "Please enter your First Name" : ""
                        }
                      />
                      <TextField
                        label="Last Name"
                        fullWidth
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        error={!lastName}
                        helperText={
                          !lastName ? "Please enter your Last Name" : ""
                        }
                      />
                    </div>
                  </>
                )}
              </Stack>

              <Grid
                item
                xs={12}
                md={4}
                marginTop={2}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="top"
              >
                {selectedPlanId && quantity > 0 && (
                  <Box
                    className="plan-box"
                    sx={{
                      width: "100%",
                      mb: 2,
                      mt: 0,
                      alignItems: "center", // This centers items horizontally
                      textAlign: "center", // This ensures the text content itself is centered
                    }}
                  >
                    <Typography variant="body2">
                      Price per license per annum: {"  "}
                      {currencyFormatter.format(pricePerItem)}
                      {discount > 0 && (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ color: "warning.main", fontWeight: "bold" }}
                        >
                          {" "}
                          (discounted by {discount}%)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: "bold" }}>
                      Total cost per annum:{" "}
                      {currencyFormatter.format(totalPrice)}
                      {discount > 0 && (
                        <Typography
                          component="span"
                          sx={{ color: "warning.main", fontWeight: "bold" }}
                        >
                          {" "}
                          (You saved{" "}
                          {currencyFormatter.format(
                            totalPrice / (1 - discount / 100) - totalPrice,
                          )}
                          )
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}

                {!showPayPal && !loadingPayPal ? (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    disabled={isPayButtonDisabled}
                    onClick={() => {
                      setLoadingPayPal(true);
                      setTimeout(() => {
                        setLoadingPayPal(false);
                        setShowPayPal(true);
                      }, 1200);
                    }}
                  >
                    Review & Pay with PayPal
                  </Button>
                ) : loadingPayPal ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    py={2}
                  >
                    <CircularProgress size={30} />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      Loading PayPal secure payment...
                    </Typography>
                  </Box>
                ) : (
                  <div
                    className="paypal-button-container"
                    style={{ width: "100%" }}
                  >
                    <CustomPayPalSubscriptionButton
                      paypalClientId={appSettings.PAYPAL_CLIENT_ID}
                      paypalPlanId={selectedPlanId}
                      quantity={quantity}
                      email={showAlternativeAddress ? emailAddress : ""}
                      firstName={showAlternativeAddress ? firstName : ""}
                      lastName={showAlternativeAddress ? lastName : ""}
                      licenseType={licenseType}
                      onClick={() => {
                        setOverlayMessage("");
                        setShowOverlay(true);
                      }}
                      onSuccess={(data) => {
                        setOverlayMessage(
                          `🎉 Subscription successful (ID: ${data.subscriptionID})! 🎉`,
                        );
                        setOverlaySubMessage(
                          "License was sent to your email. Please check your spam folder if you don't see it.",
                        );
                        setShowOverlay(true);
                        console.log("Subscription ID:", data.subscriptionID);
                        resetPayPalButtonState();
                      }}
                      onError={(err) => {
                        setOverlayMessage(
                          "⚠️ Subscription failed or was cancelled. ⚠️",
                        );
                        setOverlaySubMessage(err.message);
                        setShowOverlay(true);
                        console.error(err);
                        resetPayPalButtonState();
                      }}
                    />
                  </div>
                )}
              </Grid>
            </Paper>
          </Box>
        </ThemeProvider>
      </div>

      {/* OVERLAY MESSAGE BOX */}
      {showOverlay && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(3px)",
          }}
        >
          {/* IF NO MESSAGE => SHOW PROCESSING... */}
          {!overlayMessage && (
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 3,
                padding: 4,
                maxWidth: "90%",
                width: 400,
                boxShadow: 6,
                textAlign: "center",
              }}
            >
              <CircularProgress size={50} />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Processing subscription...
              </Typography>
            </Box>
          )}

          {/* IF MESSAGE => SHOW MESSAGE */}
          {overlayMessage && (
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 3,
                padding: 4,
                maxWidth: "90%",
                width: 600,
                boxShadow: 6,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" gutterBottom whiteSpace={"pre-line"}>
                {overlayMessage}
              </Typography>
              {overlaySubMessage && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {overlaySubMessage}
                </Typography>
              )}
              <Button
                sx={{ mt: 1 }}
                variant="contained"
                color="primary"
                onClick={() => setShowOverlay(false)}
              >
                Close
              </Button>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
}
